import { CheckedDataService, ICheckedEntity } from '@se/constraint';
import { HardcodedData, IDataSourceItem } from '@se/cp.common';
import {
    CpControlType,
    isCpEntityList,
    isEntity,
    PropertyType,
    SingleDataBindingControl,
} from 'projects/cp-framework-engine/src/lib/cp-configuration/view-model';
import { CpNodeService } from 'projects/cp-framework-engine/src/lib/cp-node.service';
import { CpDatasourceService } from 'projects/cp-framework-engine/src/lib/cp-datasource.service';

import { DataBinding } from '@se/se.ui.html5';
import { groupBy } from 'lodash-es';
import { Subscription } from 'rxjs';
import {
    CLINICAL_EVAL_COMBO_SOURCE_FOREIGN_KEY,
    NEW_LINE_CHAR,
    NEW_LINE_FOR_TEXT,
    SITE_SOURCE_FOREIGN_KEY,
} from './config';
import { ClinicalEvalExporterParams, EKineticConfig, IClinicalEvalAnswer, IKineticPrint } from './interfaces';
import {
    clearHTMLString,
    getClinicalEvalTextToPrint,
    getDescription,
    getDescriptionForPrint,
    getId,
    getKineticListHtmlRow,
    getReport,
    isClinicalEvaluationCombo,
    isClinicalEvaluationItem,
    isClinicalEvaluationItemList,
    isNotEmpty,
    sortClinicalEvalAnswer,
    sortKineticPrint,
} from './supports';
import { TranslateService } from '@ngx-translate/core';

export class ClinicalEvalExporter {
    dataService: CheckedDataService;
    dataSourceService: CpDatasourceService;
    nodeService: CpNodeService;
    translateService: TranslateService;
    allControls: CpControlType[] = [];

    /** params */
    mode: EKineticConfig = EKineticConfig.NO_LV_KINETIC;
    isHtml: boolean = false;
    siteInBold: boolean = false;
    siteSeparator: string = ': ';
    valueSeparator: string = ', ';
    leftKineticTitle: string = '';
    reversePrintKinetic: boolean = false;
    segmentLabelSeparator: string = ' ';
    segmentValueSeparator: string = ', ';
    segmentSeparator: string = '; ';
    target: string = '';
    idClinicalEvalSite: string | null = null;
    leftVentricle: IDataSourceItem | null = null;

    index: number = 0;

    get leftVentricleName(): string {
        return this.leftVentricle?.Description ?? '';
    }

    notEvaluatedItem: IDataSourceItem | null = null;

    get notEvaluatedItemName(): string {
        return this.notEvaluatedItem?.Description ?? '';
    }

    private controlsSubscription?: Subscription;

    constructor(
        dataService: CheckedDataService,
        dataSourceService: CpDatasourceService,
        nodeService: CpNodeService,
        translateService: TranslateService,
    ) {
        this.dataService = dataService;
        this.dataSourceService = dataSourceService;
        this.nodeService = nodeService;
        this.translateService = translateService;
    }

    init() {
        this.controlsSubscription = this.nodeService.getAllControlsList().subscribe((allControls) => {
            this.allControls = allControls;
        });
    }

    setParams = ([
        mode,
        isHtml,
        siteInBold,
        siteSeparator,
        valueSeparator,
        leftKineticTitle,
        reversePrintKinetic,
        segmentLabelSeparator,
        segmentValueSeparator,
        segmentSeparator,
        target,
        idClinicalEvalSite,
    ]: ClinicalEvalExporterParams): void => {
        this.target = target;
        this.mode = mode;
        this.isHtml = isHtml;
        this.siteInBold = siteInBold;
        this.siteSeparator = this.isHtml ? siteSeparator : this.replaceNewLineChar(siteSeparator);
        this.valueSeparator = this.isHtml ? valueSeparator : this.replaceNewLineChar(valueSeparator);
        this.leftKineticTitle = leftKineticTitle;
        this.reversePrintKinetic = reversePrintKinetic;
        this.segmentLabelSeparator = this.isHtml
            ? segmentLabelSeparator
            : this.replaceNewLineChar(segmentLabelSeparator);
        this.segmentValueSeparator = this.isHtml
            ? segmentValueSeparator
            : this.replaceNewLineChar(segmentValueSeparator);
        this.segmentSeparator = this.isHtml ? segmentSeparator : this.replaceNewLineChar(segmentSeparator);
        this.leftVentricle = this.getDataSourceItemById(
            HardcodedData.lists.measureSiteList,
            HardcodedData.idLeftVentricleSite,
        );
        this.notEvaluatedItem = this.getDataSourceItemById(
            HardcodedData.lists.leftVentricleKineticList,
            HardcodedData.idNotEvaluatedLeftVentricleKinetic,
        );
        this.idClinicalEvalSite = !!idClinicalEvalSite ? idClinicalEvalSite : null;
    };

    destroy() {
        this.controlsSubscription?.unsubscribe();
    }

    replaceNewLineChar = (str: string): string =>
        str.indexOf(NEW_LINE_CHAR) !== -1 ? str.replace(NEW_LINE_CHAR, NEW_LINE_FOR_TEXT) : str;

    getDataSourceItemByShortLabel = (sourceId: string, shortLabel: string): IDataSourceItem | null =>
        this.dataSourceService
            ?.getById(sourceId)
            ?.find((sourceItem) => sourceItem?.getPropertyPrimitiveValue('ShortLabel') === shortLabel) ?? null;

    getDataSourceItemById = (sourceId: string, itemId: string): IDataSourceItem | null =>
        this.dataSourceService?.getById(sourceId)?.find((sourceItem) => sourceItem.Id === itemId) || null;

    getDataSourceItem = (
        item: ICheckedEntity,
        sourceName: string,
        sourceItemIdProp: string,
    ): IDataSourceItem | null => {
        const sourceItemId = item.getPropertyPrimitiveValue(sourceItemIdProp);
        if (!sourceItemId) return null;
        return (
            this.dataSourceService?.getById(sourceName)?.find((sourceItem) => sourceItem.Id === sourceItemId) ?? null
        );
    };

    getSiteItem = (item: IDataSourceItem): IDataSourceItem | null =>
        item
            ? this.getDataSourceItem(item, HardcodedData.lists.measureSiteList, SITE_SOURCE_FOREIGN_KEY) || null
            : null;

    getClinicalEvalAnswerFromFlagControl = (control: SingleDataBindingControl): IClinicalEvalAnswer | null => {
        if (!this.dataService?.getValue) return null;
        const value: boolean = Boolean(this.dataService.getValue(control?.dataBinding));
        const sourceItemId = control.dataBinding.toString().split('@')[1];
        const flagItem: IDataSourceItem | null =
            this.dataSourceService
                ?.getById(HardcodedData.lists.clinicalEvaluationFlagList)
                ?.find((sourceItem) => sourceItem.Id === sourceItemId) || null;
        if (!flagItem) return null;
        const siteItem = this.getSiteItem(flagItem);
        if (!siteItem) return null;
        let valueString: string = '';
        const flagStringTrue: string = (flagItem?.getPropertyPrimitiveValue('ReportIfTrue') as string) || '';
        const flagStringFalse: string = (flagItem?.getPropertyPrimitiveValue('ReportIfFalse') as string) || '';
        const siteString: string = getDescription(siteItem);
        const siteId: string = getId(siteItem);
        valueString += value ? flagStringTrue : flagStringFalse;
        valueString = valueString.trim();
        if (!valueString) return null;
        return {
            siteId,
            site: siteString,
            value: valueString,
            idx: this.index++,
        };
    };

    getClinicalEvalAnswerFromStringControl = (control: SingleDataBindingControl): IClinicalEvalAnswer | null => {
        if (!this.dataService?.getValue) return null;
        const temp: PropertyType | null = this.dataService.getValue(control.dataBinding) ?? null;
        const value: string | null = temp ? String(temp) : null;
        if (!value) return null;
        const sourceItemId = control.dataBinding.toString().split('@')[1];
        const stringItem: IDataSourceItem | null =
            this.dataSourceService
                ?.getById(HardcodedData.lists.clinicalEvaluationStringList)
                ?.find((sourceItem) => sourceItem.Id === sourceItemId) ?? null;
        if (!stringItem) return null;
        const siteItem: IDataSourceItem | null = this.getSiteItem(stringItem);
        if (!siteItem) return null;
        let valueString: string = '';
        const beforeString: string = getDescriptionForPrint(stringItem);
        const siteString: string = getDescription(siteItem);
        const siteId: string = getId(siteItem);
        valueString += beforeString ? beforeString + ' ' : '';
        valueString += value ? value : '';
        valueString = valueString.trim();
        return {
            siteId,
            site: siteString,
            value: valueString,
            idx: this.index++,
        };
    };

    getClinicalEvalAnswerFromComboControl = (control: SingleDataBindingControl): IClinicalEvalAnswer | null => {
        if (!this.dataService?.getValue) return null;
        const item: PropertyType | null = this.dataService.getValue(control.dataBinding) ?? null;
        if (!item || !isEntity(item) || !isClinicalEvaluationItem(item)) return null;

        let siteItem: IDataSourceItem | null = null;

        const comboItem: IDataSourceItem | null = this.getDataSourceItem(
            item,
            HardcodedData.lists.clinicalEvaluationComboList,
            CLINICAL_EVAL_COMBO_SOURCE_FOREIGN_KEY,
        );

        if (!!comboItem && isClinicalEvaluationCombo(comboItem)) {
            siteItem = this.getSiteItem(comboItem);
        }
        let value: string = '';
        const itemString: string = getReport(item);
        const comboString: string | null = comboItem ? getDescriptionForPrint(comboItem) : null;
        const siteString: string | null = siteItem ? getDescription(siteItem) : null;
        const siteId: string | null = siteItem ? getId(siteItem) : null;
        if (!siteString || !siteId) return null;
        value += comboString ? comboString + ' ' : '';
        value += itemString ? itemString : '';
        value = value.trim();
        return {
            siteId,
            site: siteString,
            value,
            idx: this.index++,
        };
    };

    getClinicalEvalAnswerFromMultiComboControl = (control: SingleDataBindingControl): IClinicalEvalAnswer[] => {
        if (!this.dataService?.getValue) return [];
        const itemList: PropertyType | null = this.dataService.getValue(control.dataBinding) ?? null;
        if (!itemList || !isCpEntityList(itemList) || !isClinicalEvaluationItemList(itemList)) return [];
        const items = itemList.getAllEntities(new DataBinding()) ?? [];
        return items
            .map((item) => {
                let siteItem: IDataSourceItem | null = null;
                const comboItem: IDataSourceItem | null = this.getDataSourceItem(
                    item,
                    HardcodedData.lists.clinicalEvaluationComboList,
                    CLINICAL_EVAL_COMBO_SOURCE_FOREIGN_KEY,
                );

                if (!!comboItem && isClinicalEvaluationCombo(comboItem)) {
                    siteItem = this.getSiteItem(comboItem);
                }
                let value: string = '';
                const itemString: string = getReport(item);
                const comboString: string | null = comboItem ? getDescriptionForPrint(comboItem) : null;
                const siteString: string | null = siteItem ? getDescription(siteItem) : null;
                const siteId: string | null = siteItem ? getId(siteItem) : null;
                if (!siteString || !siteId) return null;
                value += comboString ? comboString + ' ' : '';
                value += itemString ? itemString : '';
                value = value.trim();
                return {
                    siteId,
                    site: siteString,
                    value,
                    idx: this.index++,
                };
            })
            .filter(isNotEmpty);
    };

    /*qui dentro ci sono le misure tra cui anche quelle relative al ventricolo sinistro
     * sotto cui andra stampata la cinetica qualora fosse selezionata*/
    getClinicalEvalList = (): IClinicalEvalAnswer[] => {
        const output: IClinicalEvalAnswer[] = [];

        /*la cinetica del ventricolo sinistro va inserita qui in mezzo nel caso in cui debbano essere stampate entrambe*/
        const filteredControls: SingleDataBindingControl[] = this.allControls.filter(
            (control) =>
                Reflect.has(control, 'dataBinding') &&
                (control as SingleDataBindingControl).dataBinding
                    .toString()
                    .indexOf(HardcodedData.bindings.ecoCv.clinicalEvaluations.toStringSimple()) !== -1,
        ) as SingleDataBindingControl[];
        this.index = 0;

        /*controlli ordinati per come li troviamo in pagina*/
        filteredControls.forEach((control) => {
            let v: IClinicalEvalAnswer | IClinicalEvalAnswer[] | null = null;

            if (
                control.dataBinding
                    .toString()
                    .indexOf(HardcodedData.bindings.ecoCv.clinicalEvaluationsCombo.toStringSimple()) !== -1
            ) {
                v = this.getClinicalEvalAnswerFromComboControl(control);
            } else if (
                control.dataBinding
                    .toString()
                    .indexOf(HardcodedData.bindings.ecoCv.clinicalEvaluationsMultiCombo.toStringSimple()) !== -1
            ) {
                v = this.getClinicalEvalAnswerFromMultiComboControl(control);
            } else if (
                control.dataBinding
                    .toString()
                    .indexOf(HardcodedData.bindings.ecoCv.clinicalEvaluationsFlag.toStringSimple()) !== -1
            ) {
                v = this.getClinicalEvalAnswerFromFlagControl(control);
            } else if (
                control.dataBinding
                    .toString()
                    .indexOf(HardcodedData.bindings.ecoCv.clinicalEvaluationsString.toStringSimple()) !== -1
            ) {
                v = this.getClinicalEvalAnswerFromStringControl(control);
            }

            if (v) {
                if (Array.isArray(v)) output.push(...v);
                else output.push(v);
            }
        });
        return output;
    };

    /*quando si dovrà stampare anche questa andra passata la stringa gia pronta alla stampa delle misure
     * in moda da aggiungerla sotto il site corretto ovvero il ventricolo sinistro*/
    getKineticList = (): IKineticPrint[] => {
        const kineticEntries = this.dataService.getEntities(HardcodedData.bindings.ecoCv.lvKinetic);
        const segments = this.dataSourceService.getById(HardcodedData.lists.leftVentricleSegmentList);
        return (
            segments
                ?.map((segment, idx) => {
                    const entity = kineticEntries?.[0].getPropertyPrimitiveValue(segment.Description) as any; //perche prendiamo [0]?
                    if (!entity) return null;
                    const answer: IKineticPrint = {
                        label: getDescriptionForPrint(segment, true),
                        value: entity.Description ?? '',
                        idx,
                    };
                    return answer;
                })
                .filter(isNotEmpty)
                .filter((item) => item.value !== this.notEvaluatedItemName) ?? []
        );
    };

    getReverseKineticList = () =>
        this.getKineticList().map((item) => ({
            label: item.value,
            value: item.label,
            idx: item.idx,
        }));

    getKineticGrouped = (): IKineticPrint[][] => {
        const kineticList = this.reversePrintKinetic ? this.getReverseKineticList() : this.getKineticList();
        const kineticGrouped = groupBy(kineticList, (item) => item.label);
        const answer = Object.values(kineticGrouped).filter((list) => !!list.length);
        sortKineticPrint(answer);
        return answer;
    };

    getKineticText = (): string => this.generateKineticToPrintText(this.getKineticGrouped());

    generateKineticToPrintText = (kineticList: IKineticPrint[][]): string => {
        const row = kineticList.map((list) =>
            list
                .map(({ label, value }, index) => {
                    return index === 0 ? `${label}${this.segmentLabelSeparator}${value}` : value;
                })
                .join(this.segmentValueSeparator),
        );
        const text = row.join(this.segmentSeparator);
        return `${this.leftKineticTitle}\n${text}`;
    };

    getKineticHTML = (): string => this.generateKineticToPrintHtml(this.getKineticGrouped());

    generateKineticToPrintHtml = (kineticList: IKineticPrint[][]): string => {
        const labelNewLine = this.segmentLabelSeparator === NEW_LINE_CHAR;
        const valueNewLine = this.segmentValueSeparator === NEW_LINE_CHAR;
        const segmentNewLine = this.segmentSeparator === NEW_LINE_CHAR;
        const htmlRow = getKineticListHtmlRow(
            kineticList,
            labelNewLine,
            valueNewLine,
            this.segmentLabelSeparator,
            this.segmentValueSeparator,
        );
        const htmlText = segmentNewLine ? htmlRow.map((row) => `<p>${row}</p>`) : htmlRow.join(this.segmentSeparator);
        const kineticTitle = this.siteInBold ? `<strong>${this.leftKineticTitle}</strong>` : this.leftKineticTitle;
        return `<p>${kineticTitle}</p>${htmlText}`;
    };

    exportText = (): string => {
        const targetIsEmpty = this.checkTargetIsEmpty();
        let nwString = this.isHtml ? this.exportHTMLText() : this.exportPlainText();

        if (!targetIsEmpty) {
            const confirmText = this.translateService.instant('ExportClinicalEvalAskToAppend');
            const res = window.confirm(confirmText); //ok to appena cancel to overwrite
            if (res) {
                const targetValue = this.getTargetValue();
                return `${targetValue}${nwString}`;
            }
        }
        return nwString;
    };

    getTargetValue = (): string => {
        if (!this.dataService?.getValue || !this.target) return '';
        const res = this.dataService.getValue(new DataBinding(this.target));
        if (typeof res !== 'string') return '';
        return res;
    };

    checkTargetIsEmpty = (): boolean => {
        const targetValue = this.getTargetValue();
        if (!targetValue) return true;
        const cleared = clearHTMLString(targetValue);
        return Boolean(cleared.length === 0);
    };

    exportHTMLText = (): string => {
        const answers: IClinicalEvalAnswer[] = [];
        switch (this.mode) {
            case EKineticConfig.ONLY_LV_KINETIC:
                {
                    answers.push({
                        siteId: '',
                        site: '',
                        value: this.getKineticHTML(),
                    });
                }
                break;
            case EKineticConfig.INCLUDE_LV_KINETIC:
                {
                    answers.push(...this.getClinicalEvalList(), {
                        siteId: HardcodedData.idLeftVentricleSite,
                        site: this.leftVentricleName ?? '',
                        value: this.getKineticHTML(),
                    });
                }
                break;
            case EKineticConfig.NO_LV_KINETIC:
            default: {
                const clinicalEvalList = !!this.idClinicalEvalSite
                    ? this.getClinicalEvalList().filter((item) => item.siteId === this.idClinicalEvalSite)
                    : this.getClinicalEvalList();
                answers.push(...clinicalEvalList);
            }
        }
        const clinicalEvalGroupedBySite = groupBy(answers, (item) => item.site);
        return this.generateClinicalEvalHtml(clinicalEvalGroupedBySite);
    };

    exportPlainText = (): string => {
        const answers: IClinicalEvalAnswer[] = [];
        switch (this.mode) {
            case EKineticConfig.ONLY_LV_KINETIC:
                {
                    answers.push({
                        siteId: '',
                        site: '',
                        value: this.getKineticText(),
                    });
                }
                break;
            case EKineticConfig.INCLUDE_LV_KINETIC:
                {
                    answers.push(...this.getClinicalEvalList(), {
                        siteId: HardcodedData.idLeftVentricleSite,
                        site: this.leftVentricleName ?? '',
                        value: this.getKineticText(),
                    });
                }
                break;
            case EKineticConfig.NO_LV_KINETIC:
            default: {
                const clinicalEvalList = !!this.idClinicalEvalSite
                    ? this.getClinicalEvalList().filter((item) => item.siteId === this.idClinicalEvalSite)
                    : this.getClinicalEvalList();
                answers.push(...clinicalEvalList);
            }
        }
        const clinicalEvalGroupedBySite = groupBy(answers, (item) => item.site);
        return this.generateClinicalEvalText(clinicalEvalGroupedBySite);
    };

    generateClinicalEvalText = (groupedBySite: Record<string, IClinicalEvalAnswer[]>): string => {
        //qui se ci fosse da aggiungere la cinetica del ventricolo sinistro andarla a cercare nel groupedBysite e leftVentricle.push(...this.getkinetic())
        let listToPrint = Object.values(groupedBySite).filter((list) => list.length);

        sortClinicalEvalAnswer(listToPrint);

        const siteSeparator = this.replaceNewLineChar(this.siteSeparator);
        const valueSeparator = this.replaceNewLineChar(this.valueSeparator);

        /** se c'è solo un sito nascondo il titolo */
        const showSite = !this.idClinicalEvalSite;

        const textToPrint = listToPrint.map((list) =>
            list
                .map((obj, i) => {
                    const site = obj.site;
                    return i === 0 && !!site && showSite ? `${site}${siteSeparator} ${obj.value}` : `${obj.value}`;
                })
                .join(`${valueSeparator} `),
        );

        return textToPrint.join('\n');
    };

    generateClinicalEvalHtml = (groupedBySite: Record<string, IClinicalEvalAnswer[]>): string => {
        const siteNewLine = this.siteSeparator === NEW_LINE_CHAR;
        const valueNewLine = this.valueSeparator === NEW_LINE_CHAR;
        const listToPrint = Object.values(groupedBySite).filter((list) => !!list.length);
        sortClinicalEvalAnswer(listToPrint);

        const textToPrint = getClinicalEvalTextToPrint(
            listToPrint,
            siteNewLine,
            valueNewLine,
            this.siteInBold,
            this.siteSeparator,
            this.valueSeparator,
            !!this.idClinicalEvalSite /** se c'è solo un sito nascondo il titolo */,
        );

        return textToPrint
            .map((text) => {
                if (this.mode === EKineticConfig.ONLY_LV_KINETIC) return `${text}`;
                else return `<p>${text}</p>`;
            })
            .join('');
    };
}
